import React, { useMemo, useState, useEffect } from 'react';
import { Alert, Platform, ScrollView, StyleSheet, Text, TextInput, View, TouchableOpacity, Modal, FlatList, useWindowDimensions } from 'react-native';
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import theme from '../../theme';
import AppButton from '../../components/AppButton';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useI18n } from '../../i18n/useI18n';
import { getBackendUrl } from '../../lib/backendUrl';
import { fetchWithRetry } from '../../lib/fetchWithRetry';
import storage from '../../lib/storage';
import profileRepo from './profileRepository';

export type UserProfile = {
  name?: string;
  phone?: string;
  country?: string;
  city?: string;
  avatarUrl?: string;
  birthdate?: string | null;
};

export default function EditProfileForm({
  user,
  lang,
  onSave,
  onDeleteAccount,
  onClose,
}: {
  user: any;
  lang?: string;
  onSave: (nextUser: any) => void;
  onDeleteAccount: () => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useI18n(lang);

  const initialProfile: UserProfile = useMemo(() => {
    const p = (user && user.profile) ? user.profile : {};
    return {
      name: p.name || '',
      phone: p.phone || '',
      country: p.country || '',
      city: p.city || '',
      avatarUrl: p.avatarUrl || '',
    };
  }, [user]);

  const [name, setName] = useState(initialProfile.name || '');
  const [phone, setPhone] = useState(initialProfile.phone || '');
  const [country, setCountry] = useState(initialProfile.country || '');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [city, setCity] = useState(initialProfile.city || '');
  const [avatarUrl, setAvatarUrl] = useState(initialProfile.avatarUrl || '');
  const [birthdate, setBirthdate] = useState<string | null>(initialProfile.birthdate || null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountEmail, setDeleteAccountEmail] = useState('');
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const { width } = useWindowDimensions();
  const stackButtons = width < 380;
  const [countryFilter, setCountryFilter] = useState('');
  const normalizedUserEmail = String(user?.email || '').trim().toLowerCase();

  function handlePhoneChange(raw: string) {
    // If user entered a country ISO2 code in country field, pass it to AsYouType
    let countryIso: string | undefined;
    if (country && country.trim().length === 2) countryIso = country.trim().toUpperCase();
      try {
        const formatter = countryIso ? new AsYouType(countryIso as any) : new AsYouType();
      const formatted = formatter.input(raw);
      setPhone(formatted);
      setPhoneError(null);
    } catch (e) {
      // fallback to raw
      setPhone(raw);
    }
  }

  const COUNTRIES: Array<{ code: string; name: string }> = [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AO', name: 'Angola' },
    { code: 'AI', name: 'Anguilla' },
    { code: 'AQ', name: 'Antarctica' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AW', name: 'Aruba' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BM', name: 'Bermuda' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BR', name: 'Brazil' },
    { code: 'IO', name: 'British Indian Ocean Territory' },
    { code: 'VG', name: 'British Virgin Islands' },
    { code: 'BN', name: 'Brunei' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'CV', name: 'Cabo Verde' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CA', name: 'Canada' },
    { code: 'KY', name: 'Cayman Islands' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CX', name: 'Christmas Island' },
    { code: 'CC', name: 'Cocos (Keeling) Islands' },
    { code: 'CO', name: 'Colombia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CD', name: 'Congo (DRC)' },
    { code: 'CG', name: 'Congo (Republic)' },
    { code: 'CK', name: 'Cook Islands' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'CI', name: 'Côte d’Ivoire' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czechia' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'EE', name: 'Estonia' },
    { code: 'SZ', name: 'Eswatini' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FK', name: 'Falkland Islands' },
    { code: 'FO', name: 'Faroe Islands' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GF', name: 'French Guiana' },
    { code: 'PF', name: 'French Polynesia' },
    { code: 'GA', name: 'Gabon' },
    { code: 'GM', name: 'Gambia' },
    { code: 'GE', name: 'Georgia' },
    { code: 'DE', name: 'Germany' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GI', name: 'Gibraltar' },
    { code: 'GR', name: 'Greece' },
    { code: 'GL', name: 'Greenland' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GP', name: 'Guadeloupe' },
    { code: 'GU', name: 'Guam' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'GG', name: 'Guernsey' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran' },
    { code: 'IQ', name: 'Iraq' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IM', name: 'Isle of Man' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'JP', name: 'Japan' },
    { code: 'JE', name: 'Jersey' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'KI', name: 'Kiribati' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Laos' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'LR', name: 'Liberia' },
    { code: 'LY', name: 'Libya' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MO', name: 'Macao' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MW', name: 'Malawi' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' },
    { code: 'ML', name: 'Mali' },
    { code: 'MT', name: 'Malta' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MQ', name: 'Martinique' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'YT', name: 'Mayotte' },
    { code: 'MX', name: 'Mexico' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'MD', name: 'Moldova' },
    { code: 'MC', name: 'Monaco' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MS', name: 'Montserrat' },
    { code: 'MA', name: 'Morocco' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'MM', name: 'Myanmar' },
    { code: 'NA', name: 'Namibia' },
    { code: 'NR', name: 'Nauru' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NC', name: 'New Caledonia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'NE', name: 'Niger' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'NU', name: 'Niue' },
    { code: 'KP', name: 'North Korea' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'NO', name: 'Norway' },
    { code: 'OM', name: 'Oman' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PW', name: 'Palau' },
    { code: 'PA', name: 'Panama' },
    { code: 'PG', name: 'Papua New Guinea' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'QA', name: 'Qatar' },
    { code: 'RE', name: 'Réunion' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russia' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'WS', name: 'Samoa' },
    { code: 'SM', name: 'San Marino' },
    { code: 'ST', name: 'Sao Tome and Principe' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SN', name: 'Senegal' },
    { code: 'RS', name: 'Serbia' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SX', name: 'Sint Maarten' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SB', name: 'Solomon Islands' },
    { code: 'SO', name: 'Somalia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'KR', name: 'South Korea' },
    { code: 'SS', name: 'South Sudan' },
    { code: 'ES', name: 'Spain' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'BL', name: 'St. Barthélemy' },
    { code: 'SH', name: 'St. Helena' },
    { code: 'KN', name: 'St. Kitts and Nevis' },
    { code: 'LC', name: 'St. Lucia' },
    { code: 'MF', name: 'St. Martin' },
    { code: 'PM', name: 'St. Pierre & Miquelon' },
    { code: 'VC', name: 'St. Vincent & Grenadines' },
    { code: 'SD', name: 'Sudan' },
    { code: 'SR', name: 'Suriname' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SY', name: 'Syria' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TJ', name: 'Tajikistan' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'TG', name: 'Togo' },
    { code: 'TK', name: 'Tokelau' },
    { code: 'TO', name: 'Tonga' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'TM', name: 'Turkmenistan' },
    { code: 'TC', name: 'Turks and Caicos Islands' },
    { code: 'TV', name: 'Tuvalu' },
    { code: 'UG', name: 'Uganda' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'UM', name: 'U.S. Outlying Islands' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VU', name: 'Vanuatu' },
    { code: 'VA', name: 'Vatican City' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'VI', name: 'U.S. Virgin Islands' },
    { code: 'WF', name: 'Wallis and Futuna' },
    { code: 'EH', name: 'Western Sahara' },
    { code: 'YE', name: 'Yemen' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'ZW', name: 'Zimbabwe' },
  ];

  const filteredCountries = useMemo(() => {
    const q = (countryFilter || '').trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [countryFilter]);

  const formattedBirthdate = useMemo(() => {
    if (!birthdate) return '';
    try {
      const d = new Date(birthdate);
      if (isNaN(d.getTime())) return birthdate;
      // Explicit locale mapping: English -> en-US (MM/DD/YYYY), Spanish -> es-ES (DD/MM/YYYY)
      const displayLocale = (lang || 'en').startsWith('es') ? 'es-ES' : 'en-US';
      const opts: any = { day: '2-digit', month: '2-digit', year: 'numeric' };
      return new Intl.DateTimeFormat(displayLocale, opts).format(d);
    } catch (_) {
      return birthdate;
    }
  }, [birthdate, lang]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const body = await profileRepo.getProfile();
        if (!mounted) return;
        const serverUser = body && body.user ? body.user : null;
        const p = serverUser && serverUser.profile ? serverUser.profile : serverUser;
        if (p) {
          setName(p.name || '');
          setPhone(p.phone || '');
          setCountry(p.country || '');
          setCity(p.city || '');
          setAvatarUrl(p.avatarUrl || p.avatar_url || '');
          setBirthdate(p.birthdate || null);
        }
      } catch (e) {
        // ignore: keep local values
      }
    })();
    return () => { mounted = false; };
  }, []);

  function countryNameFor(code?: string) {
    if (!code) return '';
    const c = COUNTRIES.find(x => x.code.toUpperCase() === code.toUpperCase());
    return c ? `${c.name} (${c.code})` : code.toUpperCase();
  }

  function handleSave() {
    const next = {
      ...(user || {}),
      profile: {
        ...(user?.profile || {}),
        name: name.trim(),
        phone: phone.trim(),
        country: country.trim(),
        city: city.trim(),
        avatarUrl: avatarUrl.trim(),
        birthdate: birthdate || null,
      },
    };

    // Validate phone if present
    if (phone && phone.trim()) {
      try {
        const countryIso = country && country.trim().length === 2 ? country.trim().toUpperCase() : undefined;
        const parsed = parsePhoneNumberFromString(phone, countryIso as any);
        if (!parsed || !parsed.isValid()) {
          setPhoneError(t('invalidPhone') || 'Invalid phone number');
          Alert.alert(t('error') || 'Error', t('invalidPhone') || 'Invalid phone number');
          return;
        }
        // normalize to international format for storage
        const normalized = parsed.formatInternational();
        setPhone(normalized);
        setPhoneError(null);
      } catch (e) {
        setPhoneError(t('invalidPhone') || 'Invalid phone number');
        Alert.alert(t('error') || 'Error', t('invalidPhone') || 'Invalid phone number');
        return;
      }
    }

    // Update local state immediately
    onSave(next);

    // Persist locally (so UI updates even if backend is not available)
    (async () => {
      try {
        await storage.setItem('kakeibo_user', next || {});
      } catch (_) {}
    })();

    // Try to persist to backend using central repo (handles auth headers)
    (async () => {
      try {
        const body = await profileRepo.updateProfile(next.profile);
        // If backend returns the updated user/profile, update local copy
        if (body && body.user) {
          onSave(body.user);
          try { await storage.setItem('kakeibo_user', body.user || {}); } catch (_) {}
        }
        Alert.alert(t('profile') || 'Profile', t('profileSaved') || 'Saved');
      } catch (e: any) {
        try {
          Alert.alert(t('profile') || 'Profile', `${t('profileSaved') || 'Saved'} (${t('pleaseWait') || 'Please wait...'}: ${e?.message || 'network'})`);
        } catch (_) {}
      }
    })();

    onClose();
  }

  function openDeleteAccountModal() {
    setDeleteAccountEmail('');
    setDeleteAccountError(null);
    setShowDeleteAccountModal(true);
  }

  function closeDeleteAccountModal() {
    if (deletingAccount) return;
    setShowDeleteAccountModal(false);
    setDeleteAccountEmail('');
    setDeleteAccountError(null);
  }

  async function handleDeleteAccount() {
    if (deleteAccountEmail.trim().toLowerCase() !== normalizedUserEmail) {
      setDeleteAccountError(t('deleteAccountEmailMismatch') || 'The email does not match this account.');
      return;
    }

    try {
      setDeletingAccount(true);
      setDeleteAccountError(null);
      await profileRepo.deleteAccount();
      closeDeleteAccountModal();
      await onDeleteAccount();
      onClose();
    } catch (e: any) {
      setDeleteAccountError(e?.message || t('deleteAccountFailed') || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  }

  return (
    <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
      <Text style={[styles.title, { color: theme.colors.textPrimary }]}>{t('editProfile') || 'Edit profile'}</Text>

      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 16 }}
        style={{ maxHeight: Platform.OS === 'web' ? 520 : undefined }}
      >
        <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('name') || 'Name'}</Text>
        <TextInput
          value={name}
          onChangeText={setName}
          style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]}
          placeholder={t('name') || 'Name'}
          placeholderTextColor={theme.colors.textSecondary}
        />

        <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('phone') || 'Phone'}</Text>
        <TextInput
          value={phone}
          onChangeText={handlePhoneChange}
          style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]}
          placeholder={t('phonePlaceholder') || 'Phone number'}
          placeholderTextColor={theme.colors.textSecondary}
          keyboardType="phone-pad"
        />

        <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('city') || 'City'}</Text>
        <TextInput
          value={city}
          onChangeText={setCity}
          style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border, width: '100%' }]}
          placeholder={t('city') || 'City'}
          placeholderTextColor={theme.colors.textSecondary}
        />

        <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('avatarUrl') || 'Avatar URL'}</Text>
        <TextInput
          value={avatarUrl}
          onChangeText={setAvatarUrl}
          style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border, width: '100%' }]}
          placeholder={t('avatarUrl') || 'https://...'}
          placeholderTextColor={theme.colors.textSecondary}
          autoCapitalize="none"
        />

        <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('birthdate') || 'Birthdate'}</Text>
        {Platform.OS === 'web' ? (
          <input
            type="date"
            value={birthdate || ''}
            onChange={(e: any) => setBirthdate(e.target.value)}
            style={{ height: 40, padding: 8, borderRadius: 8, borderWidth: 1, borderColor: theme.colors.border, backgroundColor: theme.colors.background, color: theme.colors.textPrimary, width: '100%', boxSizing: 'border-box' }}
            placeholder={t('birthdateOptional') || 'YYYY-MM-DD (optional)'}
          />
        ) : (
          <>
            <TouchableOpacity onPress={() => setShowDatePicker(true)} style={[styles.input, { justifyContent: 'center', width: '100%' }]}>
              <Text style={{ color: birthdate ? theme.colors.textPrimary : theme.colors.textSecondary }}>
                {birthdate ? formattedBirthdate : (t('birthdateOptional') || 'Birthdate (optional)')}
              </Text>
            </TouchableOpacity>
            {showDatePicker && (
              <DateTimePicker
                value={birthdate ? new Date(birthdate) : new Date(1990,0,1)}
                mode="date"
                display="default"
                onChange={(e, d) => {
                  setShowDatePicker(false);
                  if (d) {
                    const iso = d.toISOString().slice(0,10);
                    setBirthdate(iso);
                  }
                }}
                maximumDate={new Date()}
              />
            )}
          </>
        )}

        <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('country') || 'Country'}</Text>
        <TouchableOpacity onPress={() => setShowCountryPicker(true)} style={[styles.input, { justifyContent: 'center' }]}> 
          <Text style={{ color: country ? theme.colors.textPrimary : theme.colors.textSecondary }}>
            {country ? countryNameFor(country) : (t('country') || 'Country')}
          </Text>
        </TouchableOpacity>
        <Modal visible={showCountryPicker} animationType="slide" transparent>
          <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.35)', justifyContent: 'center' }}>
            <View style={{ margin: 20, backgroundColor: theme.colors.surface, borderRadius: 8, padding: 12, maxHeight: '70%' }}>
              <Text style={{ fontWeight: '800', marginBottom: 8, color: theme.colors.textPrimary }}>{t('country') || 'Country'}</Text>
              <TextInput
                value={countryFilter}
                onChangeText={setCountryFilter}
                placeholder={t('search') || 'Search...'}
                placeholderTextColor={theme.colors.textSecondary}
                style={[styles.input, { marginBottom: 8, backgroundColor: theme.colors.background, color: theme.colors.textPrimary, borderColor: theme.colors.border }]}
              />
              <FlatList
                data={filteredCountries}
                keyExtractor={i => i.code}
                renderItem={({ item }) => (
                  <TouchableOpacity onPress={() => { setCountry(item.code); setShowCountryPicker(false); }} style={{ paddingVertical: 10 }}>
                    <Text style={{ color: theme.colors.textPrimary }}>{item.name} ({item.code})</Text>
                  </TouchableOpacity>
                )}
              />
              <View style={{ height: 12 }} />
              <AppButton title={t('close')} onPress={() => setShowCountryPicker(false)} tone="secondary" variant="outline" />
            </View>
          </View>
        </Modal>

        <View style={styles.deleteSection}>
          <Text style={[styles.deleteTitle, { color: theme.colors.error }]}>{t('deleteAccount') || 'Delete account'}</Text>
          <Text style={[styles.deleteHelp, { color: theme.colors.textSecondary }]}>{t('deleteAccountWarning') || 'This permanently deletes your account and all your data, including transactions, categories, and saved devices.'}</Text>
          <View style={{ height: 8 }} />
          <AppButton
            title={deletingAccount ? (t('pleaseWait') || 'Please wait...') : (t('deleteAccount') || 'Delete account')}
            tone="error"
            onPress={openDeleteAccountModal}
            disabled={deletingAccount}
            style={styles.deleteButton}
          />
        </View>
      </ScrollView>
      <View style={stackButtons ? styles.actionsStacked : styles.actionsRow}>
        <AppButton
          title={t('save') || 'Save'}
          onPress={handleSave}
          style={stackButtons ? styles.actionButtonStacked : styles.actionButton}
        />
        {!stackButtons && <View style={{ width: 12 }} />}
        <AppButton
          title={t('cancel') || 'Cancel'}
          tone="secondary"
          variant="outline"
          onPress={onClose}
          style={stackButtons ? styles.actionButtonStacked : styles.actionButton}
        />
      </View>

      <Modal visible={showDeleteAccountModal} animationType="fade" transparent onRequestClose={closeDeleteAccountModal}>
        <View style={styles.deleteModalOverlay}>
          <View style={[styles.deleteModalCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
            <Text style={[styles.deleteModalTitle, { color: theme.colors.error }]}>{t('deleteAccount') || 'Delete account'}</Text>
            <Text style={[styles.deleteModalText, { color: theme.colors.textPrimary }]}>{t('deleteAccountWarning') || 'This immediately and permanently deletes your account and all your data, including transactions, categories, and saved devices.'}</Text>
            <Text style={[styles.deleteModalText, { color: theme.colors.textSecondary }]}>{t('deleteAccountModalBody') || 'To continue, type the email of the account you want to permanently delete.'}</Text>
            <View style={styles.deleteAccountEmailBox}>
              <Text style={[styles.deleteAccountEmailHint, { color: theme.colors.textSecondary }]}>{t('deleteAccountEmailHint') || 'Account email'}</Text>
              <Text style={[styles.deleteAccountEmailValue, { color: theme.colors.textPrimary }]}>{user?.email || ''}</Text>
            </View>
            <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('deleteAccountEmailLabel') || 'Confirm email'}</Text>
            <TextInput
              value={deleteAccountEmail}
              onChangeText={(value) => {
                setDeleteAccountEmail(value);
                if (deleteAccountError) setDeleteAccountError(null);
              }}
              style={[styles.input, styles.deleteAccountInput, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: deleteAccountError ? theme.colors.error : theme.colors.border }]}
              placeholder={t('deleteAccountEmailPlaceholder') || 'Type your email to confirm'}
              placeholderTextColor={theme.colors.textSecondary}
              autoCapitalize="none"
              keyboardType="email-address"
              editable={!deletingAccount}
            />
            {deleteAccountError ? (
              <Text style={[styles.deleteAccountError, { color: theme.colors.error }]}>{deleteAccountError}</Text>
            ) : null}
            <View style={styles.deleteModalActions}>
              <AppButton
                title={t('cancel') || 'Cancel'}
                tone="secondary"
                variant="outline"
                onPress={closeDeleteAccountModal}
                disabled={deletingAccount}
                style={styles.deleteModalButton}
              />
              <View style={{ width: 12 }} />
              <AppButton
                title={deletingAccount ? (t('pleaseWait') || 'Please wait...') : (t('deleteAccount') || 'Delete account')}
                tone="error"
                onPress={handleDeleteAccount}
                disabled={deletingAccount || !deleteAccountEmail.trim()}
                style={styles.deleteModalButton}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { width: '100%', maxWidth: 520, borderRadius: 12, borderWidth: 1, padding: 16 },
  title: { fontSize: 16, fontWeight: '800', marginBottom: 8 },
  label: { marginTop: 10, marginBottom: 4, fontWeight: '700' },
  input: { height: 40, borderWidth: 1, paddingHorizontal: 10, borderRadius: 8 },
  deleteSection: { marginTop: 20, paddingTop: 16, borderTopWidth: 1, borderTopColor: theme.colors.border },
  deleteTitle: { fontSize: 15, fontWeight: '800' },
  deleteHelp: { marginTop: 6, lineHeight: 20 },
  deleteButton: { width: '100%' },
  deleteModalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: 20 },
  deleteModalCard: { width: '100%', maxWidth: 520, alignSelf: 'center', borderWidth: 1, borderRadius: 16, padding: 18 },
  deleteModalTitle: { fontSize: 18, fontWeight: '900', marginBottom: 8 },
  deleteModalText: { lineHeight: 20, marginBottom: 10 },
  deleteAccountEmailBox: { borderWidth: 1, borderColor: theme.colors.border, backgroundColor: theme.colors.background, borderRadius: 12, padding: 12, marginTop: 4, marginBottom: 8 },
  deleteAccountEmailHint: { fontSize: 12, fontWeight: '700', marginBottom: 4 },
  deleteAccountEmailValue: { fontSize: 15, fontWeight: '800' },
  deleteAccountInput: { width: '100%' },
  deleteAccountError: { marginTop: 8, fontWeight: '700' },
  deleteModalActions: { flexDirection: 'row', alignItems: 'center', marginTop: 16 },
  deleteModalButton: { flex: 1 },
  actionsRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 },
  actionsStacked: { flexDirection: 'column', alignItems: 'stretch', marginTop: 12 },
  actionButton: { flex: 1, minWidth: 120 },
  actionButtonStacked: { width: '100%', marginTop: 8 },
});
